// Libs
import { head, isEmpty, cloneDeep } from 'lodash';

// Selectors
import {
    getClipboardElements,
    getClipboardOperation,
    getClipboardPasteCount,
    getClipboardElementsBoundingRect,
    getClipboardVisibleOnCanvas,
} from './clipboardSelectors';
import { getCurrentBoardId } from '../../../reducers/currentBoardId/currentBoardIdSelector';
import { getSelectedElements } from '../../../element/selection/selectedElementsSelector';
import { getGridSize } from '../../../utils/grid/gridSizeSelector';
import { getCurrentFocus } from '../../../reducers/focusSelector';
import { getPageIdSelector } from '../../../reducers/sessionSelector';
import { getCurrentVisibleBoardCanvasOrigin } from '../../../element/selectors/currentBoardSelector';

// Services
import { getNewTransactionId } from '../../../utils/undoRedo/undoRedoTransactionManager';
import { fetchBoards } from '../../../element/board/boardService';
import { fetchElements } from '../../../element/elementService';

// Utils
import { asObject } from '../../../../common/utils/immutableHelper';
import { getElementId, getXPosition, getYPosition } from '../../../../common/elements/utils/elementPropertyUtils';
import { canBeAColumnChild } from '../../../../common/columns/columnUtils';
import { isOverlapping } from '../../../../common/maths/geometry/rect';
import { getGridPosition } from '../../../canvas/utils/canvasPositionUtils';
import {
    hasVisibleDescendantsExcludingAttachments,
    isColumn,
    isLine,
} from '../../../../common/elements/utils/elementTypeUtils';

// Actions
import { moveMultipleElements } from '../../../element/actions/elementMoveActions';
import { duplicateMultipleElements } from '../../../element/duplicate/elementDuplicateActions';
import { deselectAllElements, setSelectedElements } from '../../../element/selection/selectionActions';

// Constants
import {
    CUT,
    ELEMENT_CLIPBOARD_PASTE,
    ELEMENT_CLIPBOARD_SAVE,
    PASTE_OFFSET_X,
    PASTE_OFFSET_Y,
} from './clipboardConstants';
import { BoardSections } from '../../../../common/boards/boardConstants';
import { ELEMENT_MOVE_OPERATIONS } from '../../../../common/elements/elementConstants';

export const saveToClipboard =
    ({ operation, elements, saveId, pasteCount, elementsBoundingRect, transactionId = getNewTransactionId() }) =>
    (dispatch, getState) => {
        if (!elements.length) return;

        if (operation === CUT) dispatch(deselectAllElements());

        dispatch({
            type: ELEMENT_CLIPBOARD_SAVE,
            operation,
            elements,
            saveId,
            elementsBoundingRect,
            pasteCount,
            sync: true,
            transactionId,
            timestamp: Date.now(),
        });
    };

const getInboxPastePosition = (element) => ({
    ...element.location.position,
    index: 0,
    score: null,
});

const getCanvasOffsetPastePosition = ({ element, pasteCount }) => ({
    ...element.location.position,
    x: element.location.position.x + PASTE_OFFSET_X * pasteCount,
    y: element.location.position.y + PASTE_OFFSET_Y * pasteCount,
});

export const getCanvasCenterPastePosition = ({ element, canvasRect, elementsBoundingRect, pasteCount }) => {
    const elementXInBoundingRect = element ? element.location.position.x - elementsBoundingRect.left : 0;
    const elementYInBoundingRect = element ? element.location.position.y - elementsBoundingRect.top : 0;
    const elementsBoundingRectWidth = elementsBoundingRect.right - elementsBoundingRect.left;
    const elementsBoundingRectHeight = elementsBoundingRect.bottom - elementsBoundingRect.top;
    const canvasRectWidth = canvasRect.right - canvasRect.left;
    const canvasRectHeight = canvasRect.bottom - canvasRect.top;
    return {
        x:
            canvasRect.left +
            canvasRectWidth / 2 - // Center x of the visible canvas
            elementsBoundingRectWidth / 2 + // Half width of elementsBoundingRect
            elementXInBoundingRect + // Element x in paste rect
            PASTE_OFFSET_X * pasteCount, // Paste offset
        y:
            canvasRect.top +
            canvasRectHeight / 2 -
            elementsBoundingRectHeight / 2 +
            elementYInBoundingRect +
            PASTE_OFFSET_X * pasteCount,
    };
};

const getCanvasPastePosition = ({ visibleOnCanvas, element, canvasRect, elementsBoundingRect, pasteCount }) =>
    visibleOnCanvas
        ? getCanvasOffsetPastePosition({ element, pasteCount })
        : getCanvasCenterPastePosition({ element, canvasRect, elementsBoundingRect, pasteCount });

const getCanvasPasteLocation =
    ({ visibleOnCanvas, currentBoardId, canvasRect, elementsBoundingRect }) =>
    ({ element, pasteCount }) => ({
        parentId: currentBoardId,
        section: element.location.section,
        position: getCanvasPastePosition({ visibleOnCanvas, element, canvasRect, elementsBoundingRect, pasteCount }),
    });

const getCanvasSpecifiedPasteLocation =
    ({ location, elementsBoundingRect, currentBoardCanvasOrigin }) =>
    ({ element }) => ({
        ...location,
        position: {
            ...location.position,
            x:
                element.location.position.x -
                elementsBoundingRect.left +
                location.position.x -
                currentBoardCanvasOrigin.x,
            y:
                element.location.position.y -
                elementsBoundingRect.top +
                location.position.y -
                currentBoardCanvasOrigin.y,
        },
    });

const getColumnPasteLocation =
    ({ columnId, canvasRect, pasteCount, elementsBoundingRect, visibleOnCanvas, isInbox }) =>
    ({ element }) =>
        isLine(element) || (!isInbox && isColumn(element))
            ? {
                  ...element.location,
                  position: getCanvasPastePosition({
                      visibleOnCanvas,
                      element,
                      canvasRect,
                      elementsBoundingRect,
                      pasteCount,
                  }),
              }
            : {
                  ...element.location,
                  parentId: columnId,
                  section: BoardSections.INBOX,
                  position: getInboxPastePosition(element),
              };

export const getCanvasRectInGridSize = (gridSize) => {
    // FIXME-ZOOM - Revisit this and ensure it's still doing the correct thing, given the structural changes
    const canvasViewportNode = head(document.getElementsByClassName('CanvasViewport'));
    const canvasRect = canvasViewportNode.getBoundingClientRect();
    const canvasTopLeft = getGridPosition(
        canvasViewportNode,
        { x: 0, y: 0 },
        gridSize,
        undefined,
        // FIXME-ZOOM
        // zoomScale,
        // zoomTranslationPx,
    );

    return {
        left: canvasTopLeft.x,
        top: canvasTopLeft.y,
        right: canvasTopLeft.x + canvasRect.width / gridSize,
        bottom: canvasTopLeft.y + canvasRect.height / gridSize,
    };
};

const handlePasteElements =
    ({ operation, sessionId, elements, pasteCount, pasteLocationFn, shouldSelect, visibleOnCanvas }) =>
    async (dispatch, getState) => {
        const state = getState();

        const currentPageId = getPageIdSelector(state);

        if (sessionId && currentPageId !== sessionId) {
            const containerIdsToFetch = elements.filter(hasVisibleDescendantsExcludingAttachments).map(getElementId);

            if (!isEmpty(containerIdsToFetch)) {
                await dispatch(
                    fetchBoards({
                        boardIds: containerIdsToFetch,
                        force: true,
                        loadAncestors: true,
                    }),
                );
            }

            const elementIdsToFetch = elements
                .filter((el) => !hasVisibleDescendantsExcludingAttachments(el))
                .map(getElementId);

            if (!isEmpty(elementIdsToFetch)) {
                await dispatch(
                    fetchElements({
                        elementIds: elementIdsToFetch,
                        force: true,
                        loadAncestors: true,
                    }),
                );
            }
        }

        if (operation === CUT) {
            const moves = elements.map((element) => ({
                id: element.id,
                location: pasteLocationFn({ element, pasteCount }),
            }));

            dispatch(moveMultipleElements({ moves, moveOperation: ELEMENT_MOVE_OPERATIONS.CUT }));
            dispatch(setSelectedElements({ ids: moves.map((move) => move.id) }));
        } else {
            const duplications = elements.map((element) => ({
                ...element,
                location: pasteLocationFn({ element, pasteCount }),
            }));
            dispatch(duplicateMultipleElements({ duplications, shouldSelect }));
        }

        dispatch({
            type: ELEMENT_CLIPBOARD_PASTE,
            pasteCount,
            visibleOnCanvas,
            sync: true,
        });
    };

/**
 * If the clipboard elements are copied from a board that has a canvasOrigin that's been shifted, then the
 * position of the elements might be outside the visible area of the current board.
 *
 * In this case, we want to shift those elements to be inside the canvas origin of the current board (with
 * the same offset from the top left).
 */
const repositionClipboardElementsForPaste = (clipboardElements, currentBoardCanvasOrigin, elementsBoundingRect) => {
    const clipboardElementsTopLeft = clipboardElements.reduce(
        (acc, el) => {
            const x = getXPosition(el);
            const y = getYPosition(el);

            if (x < acc.x) {
                acc.x = x;
            }

            if (y < acc.y) {
                acc.y = y;
            }

            return acc;
        },
        { x: Infinity, y: Infinity },
    );

    const positionTranslation = {
        // NOTE: The elementsBoundingRect gives us the offset for the element from the top left
        x: currentBoardCanvasOrigin.x - clipboardElementsTopLeft.x + elementsBoundingRect.left,
        y: currentBoardCanvasOrigin.y - clipboardElementsTopLeft.y + elementsBoundingRect.top,
    };

    if (!positionTranslation.x && !positionTranslation.y) return clipboardElements;

    return clipboardElements.map((el) => {
        const shiftedElement = cloneDeep(el);

        if (shiftedElement.location.position) {
            shiftedElement.location.position.x += positionTranslation.x;
            shiftedElement.location.position.y += positionTranslation.y;
        }

        return shiftedElement;
    });
};

export const pasteElements =
    ({ elements, location, sessionId }) =>
    (dispatch, getState) => {
        const state = getState();
        const operation = getClipboardOperation(state);
        const clipboardElements = elements || getClipboardElements(getState()).toJS();
        const selectedElements = getSelectedElements(state);
        const currentBoardId = getCurrentBoardId(state);
        const currentFocus = getCurrentFocus(state);
        const prevPasteCount = getClipboardPasteCount(state);
        const gridSize = getGridSize(state);
        const prevVisibleOnCanvas = getClipboardVisibleOnCanvas(state);
        const elementsBoundingRect = getClipboardElementsBoundingRect(state).toJS();
        const canvasRect = getCanvasRectInGridSize(gridSize);
        const visibleOnCanvas = isOverlapping(elementsBoundingRect, canvasRect);
        const pasteCount = prevVisibleOnCanvas === visibleOnCanvas ? prevPasteCount + 1 : 0;
        const currentBoardCanvasOrigin = asObject(getCurrentVisibleBoardCanvasOrigin(state));

        if (!clipboardElements.length) return;

        const translatedClipboardElements = repositionClipboardElementsForPaste(
            clipboardElements,
            currentBoardCanvasOrigin,
            elementsBoundingRect,
        );

        const isDestinationColumn =
            selectedElements.size === 1 &&
            selectedElements.every(isColumn) &&
            translatedClipboardElements.every(canBeAColumnChild);

        // Paste in columns if all of the selected elements are columns
        if (isDestinationColumn) {
            return selectedElements.forEach((column) => {
                dispatch(
                    handlePasteElements({
                        operation,
                        sessionId,
                        elements: translatedClipboardElements,
                        pasteCount,
                        pasteLocationFn: getColumnPasteLocation({
                            columnId: getElementId(column),
                            canvasRect,
                            elementsBoundingRect,
                            pasteCount,
                            visibleOnCanvas,
                            gridSize,
                            isInbox: false,
                        }),
                        shouldSelect: false,
                        visibleOnCanvas,
                    }),
                );
            });
        }

        // Paste in unsorted notes if current focus is on unsorted notes
        if (currentFocus === BoardSections.INBOX) {
            return dispatch(
                handlePasteElements({
                    operation,
                    sessionId,
                    elements: translatedClipboardElements,
                    pasteCount,
                    pasteLocationFn: getColumnPasteLocation({
                        columnId: currentBoardId,
                        canvasRect,
                        elementsBoundingRect,
                        pasteCount,
                        visibleOnCanvas,
                        gridSize,
                        isInbox: true,
                    }),
                    shouldSelect: true,
                    visibleOnCanvas,
                }),
            );
        }

        // If provided a location then paste accordingly, otherwise if original elements are visible then paste
        // next to them, otherwise paste in the centre
        const pasteLocationFn = location
            ? getCanvasSpecifiedPasteLocation({ location, elementsBoundingRect, currentBoardCanvasOrigin })
            : getCanvasPasteLocation({ visibleOnCanvas, currentBoardId, canvasRect, elementsBoundingRect, gridSize });

        return dispatch(
            handlePasteElements({
                operation,
                sessionId,
                elements: translatedClipboardElements,
                pasteCount,
                pasteLocationFn,
                shouldSelect: true,
                visibleOnCanvas,
            }),
        );
    };
